import React, { useState, useEffect } from 'react';
import { Avatar } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';

/**
 * Brand-colored avatar that falls back to a gradient tile with the user's
 * initials when no profile image is available OR when the image URL fails
 * to load. Mirrors the header avatar so users / customers / chat contacts
 * look consistent across the app.
 *
 * Props:
 *   name         - person's name; first letters become the initials
 *   src          - optional image URL; when missing/blank/broken, initials show
 *   size         - pixel size; defaults to 40
 *   initialsLen  - number of initial letters to render; defaults to 2
 *   sx           - extra MUI sx to merge on top
 *   variant      - 'circular' | 'rounded' | 'square'
 *   gradient     - override the default burgundy gradient
 */
const SKIP_PATTERNS = [
  'no-image',
  'noimage',
  'no_image',
  'noavatar',
  'no-avatar',
  'no_avatar',
  'no-photo',
  'no_photo',
  'nophoto',
  'default-avatar',
  'default_avatar',
  'default-user',
  'default_user',
  'default-customer',
  'default_customer',
  'default-profile',
  'default_profile',
  'placeholder',
  'maps.googleapis',
  'maps.google',
  'maps.gstatic',
  'gstatic.com/maps',
  'staticmap',
  'maptile',
  'satellite',
  'mapbox',
  'openstreetmap',
  'gravatar',
  'identicon',
  'monsterid',
  'robohash',
  'dicebear',
  'ui-avatars',
  'world-map',
  'globe',
  'earth-icon',
];

// Common image extensions we trust for real photos.
const PHOTO_EXTENSIONS = /\.(jpe?g|png|webp|avif|heic|heif)(\?.*)?$/i;

const isValidUrl = (v) => {
  if (!v) return false;
  const s = String(v).trim();
  if (!s) return false;
  if (s === 'null' || s === 'undefined' || s === 'false') return false;
  const lower = s.toLowerCase();
  if (SKIP_PATTERNS.some((p) => lower.includes(p))) return false;
  // Strip query string before checking the filename for a default-like name.
  const path = lower.split('?')[0];
  const lastSegment = path.substring(path.lastIndexOf('/') + 1);
  // Reject filenames that are just "default", "avatar", "user", "customer", "profile" etc.
  if (/^(default|avatar|user|customer|profile|placeholder|icon|thumb|thumbnail)(-?\d*)?\.[a-z]+$/.test(lastSegment)) {
    return false;
  }
  // Require a real photo-like extension; if URL has NO extension at all, treat as untrusted.
  if (!PHOTO_EXTENSIONS.test(path)) {
    return false;
  }
  return true;
};

const BrandAvatar = ({
  name,
  src,
  size = 40,
  initialsLen = 2,
  sx = {},
  variant = 'circular',
  gradient = 'linear-gradient(135deg,#A82175 0%,#7B1C4A 100%)',
  ...rest
}) => {
  const safeName = (name || '').toString().trim();
  const initials = safeName
    ? safeName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, initialsLen)
        .map((p) => p.charAt(0).toUpperCase())
        .join('')
    : '';

  const hasSrc = isValidUrl(src);
  const hasInitials = initials.length > 0;
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [src]);

  const showImage = hasSrc && !broken;

  return (
    <Avatar
      alt={safeName || 'User'}
      src={showImage ? src : undefined}
      variant={variant}
      imgProps={{ onError: () => setBroken(true) }}
      sx={{
        width: size,
        height: size,
        fontSize: Math.max(11, Math.round(size * 0.4)),
        fontWeight: 700,
        color: '#fff',
        background: `${gradient} !important`,
        bgcolor: 'transparent !important',
        '& .MuiAvatar-fallback': { color: '#fff' },
        ...sx,
      }}
      {...rest}
    >
      {hasInitials ? initials : <PersonOutlineIcon sx={{ fontSize: Math.round(size * 0.6), color: '#fff' }} />}
    </Avatar>
  );
};

export default BrandAvatar;
